<script setup lang="ts">
import { buildAccountScopedPath } from '@threadline/shared'
import { watch, watchEffect } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useTheme } from 'vuetify'
import DevelopmentToolsPanel from './components/DevelopmentToolsPanel.vue'
import InstallPromotionBanner from './components/InstallPromotionBanner.vue'
import { useAuthenticationSession } from './composables/useAuthenticationSession.ts'
import { useColorScheme } from './composables/useColorScheme.ts'
import { useIncomingMessageNotifications } from './composables/useIncomingMessageNotifications.ts'
import { useQuietConversations } from './composables/useQuietConversations.ts'
import {
  startRealtimeEventStream,
  stopRealtimeEventStream,
  useRealtimeEvents,
} from './composables/useRealtimeEvents.ts'

const { t: translate } = useI18n()
const applicationRouter = useRouter()
const { authenticatedUser, isAuthenticated, notificationSoundName } = useAuthenticationSession()
const { conversationStaysQuiet } = useQuietConversations()

/**
 * Light or dark is decided here, at the root, because it is a property of the window rather
 * than of any screen inside it — and because the device may change its mind at dusk while
 * a tab has been open since morning.
 */
const vuetifyTheme = useTheme()
const { activeThemeName } = useColorScheme()
watchEffect(() => {
  vuetifyTheme.change(activeThemeName.value)
  // The browser's own furniture — form controls, scrollbars, the address bar on mobile —
  // follows this rather than the palette, so it has to be told separately.
  document.documentElement.style.colorScheme = vuetifyTheme.current.value.dark ? 'dark' : 'light'
})

/**
 * Incoming messages are announced from the root rather than from the chat view, so a
 * message is heard whatever screen the person is on.
 */
const { announceRealtimeEvent, prepareNotificationSound } = useIncomingMessageNotifications({
  viewerHandle: () => authenticatedUser.value?.handle ?? null,
  conversationStaysQuiet,
  soundName: () => notificationSoundName.value,
  translate: (localizationKey, namedValues) => translate(localizationKey, namedValues ?? {}),
  openConversation: (destinationPath, messageId) => {
    // The notification belongs to the account that received the message, so it opens
    // inside that account rather than in whichever one this tab happens to show.
    const viewer = authenticatedUser.value
    void applicationRouter.push({
      path:
        viewer === null
          ? destinationPath
          : buildAccountScopedPath(viewer.accountId, viewer.alias, destinationPath),
      query: { highlightMessageId: String(messageId) },
    })
  },
})

useRealtimeEvents({ onRealtimeEvent: announceRealtimeEvent })

watch(
  isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      // Prepared here rather than when the first message arrives, and that ordering is the
      // whole point: browsers refuse to play audio until a gesture has reached the element,
      // so an element created at the moment of the first message has never seen one. It is
      // silent, and stays silent until the person happens to click again. Creating it now
      // means the next click arms it, and the first message is already audible.
      prepareNotificationSound(notificationSoundName.value)
      startRealtimeEventStream()
    } else {
      stopRealtimeEventStream()
    }
  },
  { immediate: true },
)
</script>

<template>
  <v-app>
    <a class="skip-to-main-content" href="#main-content">{{
      translate('a11y.skipToMainContent')
    }}</a>
    <router-view />
    <InstallPromotionBanner />
    <DevelopmentToolsPanel />
  </v-app>
</template>

<style>
html,
body {
  height: 100%;
}

.skip-to-main-content {
  position: absolute;
  left: -10000px;
  top: 0;
  z-index: 100;
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-primary));
  padding: 12px 16px;
}

.skip-to-main-content:focus {
  left: 8px;
  top: 8px;
}

:focus-visible {
  outline: 3px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}
</style>
